import { 
    Box, 
    Typography, 
    Chip,
    Paper,
    Tooltip,
    alpha
} from '@mui/material';
import { useSelector } from 'react-redux';
import { itemMutator, itemsNormalizer } from '../../../utils/converters';
import { problemColors } from '../../shared/helper';

// Иконки для разных типов данных
import DataObjectIcon from '@mui/icons-material/DataObject';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import SpeedIcon from '@mui/icons-material/Speed';
import SensorsIcon from '@mui/icons-material/Sensors';
import StorageIcon from '@mui/icons-material/Storage';

const ComplexData = props => {
    const { host, hostType = 'default' } = props;
    const isDarkMode = useSelector(state => state.settings.darkMode);

    // Функция для получения иконки по типу данных
    const getTypeIcon = (typeName) => {
        const name = typeName.toLowerCase();
        if (name.includes('темп') || name.includes('temp')) return ThermostatIcon;
        if (name.includes('скор') || name.includes('speed')) return SpeedIcon;
        if (name.includes('сенсор') || name.includes('sensor')) return SensorsIcon;
        if (name.includes('бд') || name.includes('database')) return StorageIcon;
        return DataObjectIcon;
    };

    // Функция для получения приоритета проблемы
    const getProblemPriority = (item, triggers) => {
        const trigger = triggers?.find(tr => tr.items?.find(it => it.itemid === item.itemid));
        return trigger ? parseInt(trigger.priority) : 0;
    };

    // Функция для проверки наличия проблем у элемента
    const hasItemProblem = (item, triggers) => {
        return triggers?.some(tr => tr.items?.find(it => it.itemid === item.itemid));
    };

    // Функция для получения названия приоритета
    const getPriorityName = (priority) => {
        switch (priority) {
            case '0': return 'Не определено';
            case '1': return 'Информация';
            case '2': return 'Предупреждение';
            case '3': return 'Средняя';
            case '4': return 'Высокая';
            case '5': return 'Критическая';
            default: return 'Не определено';
        }
    };

    // Функция для группировки элементов по типам
    const groupItemsByType = (items) => {
        const types_names = new Set();
        const types = {};
        
        items.forEach(item => {
            let t = item.name.indexOf('.') !== -1 ? item.name.split('.')[0] : 
                   (hostType === 'crossroad' ? 'Данные БД' : 'Общие');
            types_names.add(t);
            types[t] = !types[t] ? [item] : [item, ...types[t]];
        });
        
        return {
            types_names: Array.from(types_names).sort((a, b) => a.localeCompare(b)),
            types
        };
    };

    // Функция для рендеринга элемента данных
    const renderDataItem = (item, itemKey) => {
        const item_converted = itemMutator(item);
        
        // Проверяем триггеры основного хоста или дочернего хоста
        let trigger = null;
        if (item._childTriggers) {
            trigger = item._childTriggers?.find(tr => tr.items?.find(it => it.itemid === item.itemid));
        } else {
            trigger = host.triggers?.find(tr => tr.items?.find(it => it.itemid === item.itemid));
        }
        
        let statusColor = 'var(--text-primary)';
        let hasProblem = false;
        let problemDescription = '';
        
        if (trigger) {
            statusColor = problemColors[trigger.priority];
            hasProblem = true;
            
            if (trigger.description) {
                problemDescription = trigger.description;
            }
        }

        if (item.status === "0") {
            const itemName = item.name.indexOf('.') !== -1 ? item.name.split('.')[1] : item.name;
            // Полное значение для тултипа
            const fullValue = `${item_converted.lastvalue} ${item_converted.units}`;
            // Обрезанное значение для отображения (для crossroad обрезаем до 50 символов)
            const displayValue = hostType === 'crossroad' && item_converted.lastvalue.length > 50
                ? `${item_converted.lastvalue.substring(0, 50)}... ${item_converted.units}`
                : fullValue;

            return (
                <Box
                    key={itemKey}
                    sx={{
                        p: { xs: 0.75, md: 1 },
                        mb: { xs: 0.4, md: 0.5 },
                        backgroundColor: hasProblem 
                            ? alpha(statusColor, 0.08)
                            : isDarkMode ? 'var(--background-subtle)' : 'var(--background-paper)',
                        border: `1px solid ${hasProblem 
                            ? alpha(statusColor, 0.3) 
                            : 'var(--border-default)'}`,
                        borderRadius: { xs: '6px', md: '8px' },
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            backgroundColor: hasProblem 
                                ? alpha(statusColor, 0.12)
                                : 'var(--hover-bg)',
                            borderColor: hasProblem ? statusColor : 'var(--primary-color)',
                            transform: 'translateX(2px)'
                        },
                        ...(hasProblem && {
                            borderLeft: { xs: `2px solid ${statusColor}`, md: `3px solid ${statusColor}` }
                        })
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: { xs: 0.75, md: 1 } }}>
                        <Typography 
                            sx={{ 
                                fontWeight: hasProblem ? 600 : 500,
                                color: hasProblem ? statusColor : 'var(--text-primary)',
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                lineHeight: 1.3,
                                flex: 1,
                                minWidth: 0,
                                wordBreak: 'break-word'
                            }}
                        >
                            {itemName}
                        </Typography>
                        
                        <Tooltip 
                            title={fullValue} 
                            placement="top"
                            arrow
                            enterDelay={300}
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: 'var(--background-paper)',
                                        color: 'var(--text-primary)',
                                        border: '1px solid var(--border-default)',
                                        boxShadow: 'var(--shadow-lg)',
                                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                                        fontFamily: 'monospace',
                                        maxWidth: { xs: 300, md: 400 },
                                        wordBreak: 'break-all'
                                    }
                                },
                                arrow: {
                                    sx: {
                                        color: 'var(--background-paper)',
                                        '&::before': {
                                            border: '1px solid var(--border-default)'
                                        }
                                    }
                                }
                            }}
                        >
                            <Typography 
                                sx={{ 
                                    color: hasProblem ? statusColor : 'var(--text-secondary)',
                                    fontWeight: hasProblem ? 600 : 400,
                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                    lineHeight: 1.2,
                                    textAlign: 'right',
                                    fontFamily: 'monospace',
                                    maxWidth: '50%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    cursor: 'help'
                                }}
                            >
                                {displayValue}
                            </Typography>
                        </Tooltip>
                    </Box>
                    
                    {hasProblem && (
                        <Box sx={{ mt: { xs: 0.4, md: 0.5 }, display: 'flex', flexDirection: 'column', gap: { xs: 0.2, md: 0.25 } }}>
                            <Chip
                                label={getPriorityName(trigger.priority)}
                                size="small"
                                sx={{
                                    height: { xs: 16, md: 18 },
                                    width: 'fit-content',
                                    fontSize: { xs: '0.55rem', md: '0.6rem' },
                                    fontWeight: 600,
                                    backgroundColor: alpha(statusColor, 0.15),
                                    color: statusColor,
                                    border: `1px solid ${alpha(statusColor, 0.3)}`,
                                    '& .MuiChip-label': { px: { xs: 0.5, md: 0.75 } }
                                }}
                            />
                            {problemDescription && (
                                <Typography 
                                    sx={{ 
                                        color: 'var(--text-muted)',
                                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                                        fontStyle: 'italic',
                                        lineHeight: 1.2
                                    }}
                                >
                                    {problemDescription}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            );
        }
        return null;
    };

    // Функция для рендеринга карточки типа
    const renderTypeCard = (typeName, items) => {
        const sortedItems = [...items].sort((a, b) => {
            const aName = a.name.split('.')[1] || a.name;
            const bName = b.name.split('.')[1] || b.name;
            return aName.localeCompare(bName);
        });

        const IconComponent = getTypeIcon(typeName);
        const problemItems = sortedItems.filter(item => hasItemProblem(item, host.triggers));
        const hasProblems = problemItems.length > 0;
        
        const maxPriority = hasProblems ? Math.max(...problemItems.map(item => getProblemPriority(item, host.triggers))) : 0;
        const problemColor = hasProblems ? problemColors[maxPriority] : null;

        return (
            <Paper 
                key={typeName}
                elevation={0}
                sx={{ 
                    height: 'fit-content',
                    borderRadius: { xs: '8px', md: '12px' },
                    backgroundColor: 'var(--background-paper)',
                    border: `1px solid ${hasProblems 
                        ? alpha(problemColor, 0.4) 
                        : 'var(--border-default)'}`,
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        boxShadow: isDarkMode 
                            ? '0 4px 16px rgba(0, 0, 0, 0.3)' 
                            : '0 4px 16px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                {/* Заголовок карточки */}
                <Box sx={{
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, md: 1.5 },
                    backgroundColor: hasProblems 
                        ? alpha(problemColor, 0.06)
                        : 'var(--background-subtle)',
                    borderBottom: `1px solid ${hasProblems 
                        ? alpha(problemColor, 0.2) 
                        : 'var(--border-default)'}`
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: 28, md: 32 },
                            height: { xs: 28, md: 32 },
                            borderRadius: { xs: '6px', md: '8px' },
                            backgroundColor: hasProblems ? problemColor : 'var(--primary-color)',
                            color: 'white',
                        }}
                    >
                        <IconComponent sx={{ fontSize: { xs: 16, md: 18 } }} />
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, flexWrap: 'wrap' }}>
                            <Typography sx={{ 
                                fontSize: { xs: '0.8rem', md: '0.9rem' }, 
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                lineHeight: 1.2
                            }}>
                                {typeName}
                            </Typography>
                            {hasProblems && (
                                <Chip
                                    label={`${problemItems.length}`}
                                    size="small"
                                    sx={{
                                        height: { xs: 18, md: 20 },
                                        minWidth: { xs: 20, md: 24 },
                                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                                        fontWeight: 700,
                                        backgroundColor: problemColor,
                                        color: 'white',
                                        '& .MuiChip-label': { px: { xs: 0.4, md: 0.5 } }
                                    }}
                                />
                            )}
                        </Box>
                        <Typography sx={{ 
                            fontSize: { xs: '0.65rem', md: '0.7rem' }, 
                            color: 'var(--text-muted)',
                            mt: 0.25
                        }}>
                            {sortedItems.length} {sortedItems.length === 1 ? 'параметр' : 
                                sortedItems.length < 5 ? 'параметра' : 'параметров'}
                        </Typography>
                    </Box>
                </Box>
                
                {/* Содержимое карточки */}
                <Box sx={{ p: { xs: 0.75, md: 1 } }}>
                    {sortedItems.map(item => renderDataItem(item, item.itemid))}
                    
                    {sortedItems.length === 0 && (
                        <Typography 
                            sx={{ 
                                color: 'var(--text-muted)', 
                                fontStyle: 'italic',
                                textAlign: 'center',
                                py: { xs: 1.5, md: 2 },
                                fontSize: { xs: '0.75rem', md: '0.8rem' }
                            }}
                        >
                            Нет доступных данных
                        </Typography>
                    )}
                </Box>
            </Paper>
        );
    };

    // Функция для рендеринга карточки дочернего хоста
    const renderChildCard = (childHost) => {
        const childName = childHost.name.split('/')[1];
        const childItems = itemsNormalizer(childHost).items;
        
        const childProblemItems = childItems.filter(item => hasItemProblem(item, childHost.triggers));
        const childHasProblems = childProblemItems.length > 0;
        
        const childMaxPriority = childHasProblems 
            ? Math.max(...childProblemItems.map(item => getProblemPriority(item, childHost.triggers))) 
            : 0;
        const childProblemColor = childHasProblems ? problemColors[childMaxPriority] : null;

        return (
            <Paper 
                key={childName}
                elevation={0}
                sx={{ 
                    height: 'fit-content',
                    borderRadius: { xs: '8px', md: '12px' },
                    backgroundColor: 'var(--background-paper)',
                    border: `1px solid ${childHasProblems 
                        ? alpha(childProblemColor, 0.4) 
                        : 'var(--border-default)'}`,
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        boxShadow: isDarkMode 
                            ? '0 4px 16px rgba(0, 0, 0, 0.3)' 
                            : '0 4px 16px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                {/* Заголовок */}
                <Box sx={{
                    p: { xs: 1, md: 1.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, md: 1.5 },
                    backgroundColor: childHasProblems 
                        ? alpha(childProblemColor, 0.06)
                        : 'var(--background-subtle)',
                    borderBottom: `1px solid ${childHasProblems 
                        ? alpha(childProblemColor, 0.2) 
                        : 'var(--border-default)'}`
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: 28, md: 32 },
                            height: { xs: 28, md: 32 },
                            borderRadius: { xs: '6px', md: '8px' },
                            backgroundColor: childHasProblems 
                                ? childProblemColor 
                                : 'var(--secondary-color)',
                            color: 'white',
                        }}
                    >
                        <SensorsIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, flexWrap: 'wrap' }}>
                            <Typography sx={{ 
                                fontSize: { xs: '0.8rem', md: '0.9rem' }, 
                                fontWeight: 600,
                                color: 'var(--text-primary)',
                                lineHeight: 1.2
                            }}>
                                {childName}
                            </Typography>
                            {childHasProblems && (
                                <Chip
                                    label={`${childProblemItems.length}`}
                                    size="small"
                                    sx={{
                                        height: { xs: 18, md: 20 },
                                        minWidth: { xs: 20, md: 24 },
                                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                                        fontWeight: 700,
                                        backgroundColor: childProblemColor,
                                        color: 'white',
                                        '& .MuiChip-label': { px: { xs: 0.4, md: 0.5 } }
                                    }}
                                />
                            )}
                        </Box>
                        <Typography sx={{ 
                            fontSize: { xs: '0.65rem', md: '0.7rem' }, 
                            color: 'var(--text-muted)',
                            mt: 0.25
                        }}>
                            Дочерний элемент
                        </Typography>
                    </Box>
                </Box>
                
                {/* Содержимое */}
                <Box sx={{ p: { xs: 0.75, md: 1 } }}>
                    {childItems.map(item => {
                        const itemWithChildTriggers = {
                            ...item,
                            _childTriggers: childHost.triggers
                        };
                        return renderDataItem(itemWithChildTriggers, item.itemid);
                    })}
                </Box>
            </Paper>
        );
    };

    // Основная логика рендеринга
    const { types_names, types } = groupItemsByType(host.items);

    return (
        <Box sx={{ 
            p: { xs: 1, md: 1.5 }, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { 
                background: 'var(--background-subtle)', 
                borderRadius: '3px' 
            },
            '&::-webkit-scrollbar-thumb': { 
                background: 'var(--border-default)', 
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)'
                },
                gap: { xs: 1, md: 1.5 },
                alignItems: 'start'
            }}>
                {types_names.map(typeName => renderTypeCard(typeName, types[typeName]))}
                
                {hostType === 'crossroad' && host.child && 
                    [...host.child]
                        .sort((a, b) => a.name.split(' / ')[1]?.localeCompare(b.name.split(' / ')[1]))
                        .map(childHost => renderChildCard(childHost))
                }
            </Box>
            
            {types_names.length === 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)'
                    }}
                >
                    <DataObjectIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                    <Typography sx={{ 
                        fontSize: { xs: '0.875rem', md: '1rem' }, 
                        fontWeight: 500, 
                        color: 'var(--text-primary)', 
                        mb: 0.5 
                    }}>
                        Нет доступных данных
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                        Данные для отображения отсутствуют или еще загружаются
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default ComplexData;
